import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { FileText, Users, Package, MailCheck, AlertTriangle, TrendingUp, Inbox } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  if (!summary) {
    return (
      <AdminLayout title="Dashboard">
        <div className="text-[color:var(--ivory-muted)]">Loading…</div>
      </AdminLayout>
    );
  }

  const cards = [
    { label: "Revenue this month", value: formatMoney(summary.revenue_this_month_sek, "SEK"), icon: TrendingUp, accent: "green" },
    { label: "Outstanding (SEK)", value: formatMoney(summary.outstanding_sek, "SEK"), icon: AlertTriangle, accent: "red" },
    { label: "Total invoices", value: summary.invoices_total, icon: FileText },
    { label: "Customers", value: summary.customers_count, icon: Users },
    { label: "Services", value: summary.services_count, icon: Package },
    { label: "Subscribers", value: summary.subscribers_count, icon: MailCheck },
    { label: "Unread enquiries", value: summary.unread_contact, icon: Inbox, accent: summary.unread_contact > 0 ? "red" : undefined },
    { label: "Overdue invoices", value: summary.overdue_count, icon: AlertTriangle, accent: summary.overdue_count > 0 ? "red" : undefined },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-cards">
        {cards.map((c, i) => {
          const Icon = c.icon;
          const accentClass =
            c.accent === "green"
              ? "border-[color:var(--bd-green)]/30 bg-[color:var(--bd-green)]/5"
              : c.accent === "red"
              ? "border-[color:var(--bd-red)]/30 bg-[color:var(--bd-red)]/5"
              : "border-white/10 bg-white/[0.02]";
          return (
            <div
              key={i}
              className={`p-5 rounded-2xl border ${accentClass}`}
              data-testid={`dashboard-card-${i}`}
            >
              <div className="flex items-start justify-between">
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)]">{c.label}</div>
                <Icon className="w-4 h-4 text-[color:var(--ivory-muted)]" />
              </div>
              <div className="font-display text-3xl mt-3">{c.value}</div>
            </div>
          );
        })}
      </div>

      {summary.revenue_paid_bdt > 0 || summary.outstanding_bdt > 0 ? (
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)]">Total paid (BDT)</div>
            <div className="font-display text-3xl mt-3">{formatMoney(summary.revenue_paid_bdt, "BDT")}</div>
          </div>
          <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)]">Outstanding (BDT)</div>
            <div className="font-display text-3xl mt-3">{formatMoney(summary.outstanding_bdt, "BDT")}</div>
          </div>
        </div>
      ) : null}

      <div className="mt-10 grid md:grid-cols-3 gap-4">
        <QuickLink to="/admin/invoices?new=1" label="New invoice" sub="Create & send" />
        <QuickLink to="/admin/customers" label="Customers" sub="Manage your client list" />
        <QuickLink to="/admin/services" label="Services" sub="Tour packages & flight types" />
      </div>
    </AdminLayout>
  );
}

function QuickLink({ to, label, sub }) {
  return (
    <Link
      to={to}
      className="block p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[color:var(--bd-green)]/30 transition"
      data-testid={`quick-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="font-display text-xl">{label}</div>
      <div className="text-sm text-[color:var(--ivory-muted)] mt-1">{sub}</div>
    </Link>
  );
}
